/*
 * @author        zach.william@zumper.com (Zach William)
 * @copyright     Copyright (c) 2021, Zumper
 * @description   Emotion styles for the DateRangeInput
 */

import { Theme, css } from '@emotion/react'

export const calendarInputContainer = ({ colors, spacing, typography }: Theme) => css`
  min-width: 324px;
  display: flex;
  flex-direction: column;
  padding: ${spacing.spacing4};
  background-color: ${colors.backgroundLight};
  border-radius: 5px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  justify-content: space-between;
  font-family: ${typography.bodyFontFamily};
  width: 100%;
`

export const transparentBackground = css`
  background-color: transparent;
  box-shadow: none;
  padding: 0;
`

export const calendarArrowContainer = css`
  flex-direction: row;
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
  margin-bottom: 1rem;
  align-items: center;
`

export const arrowButtonLeft = css`
  background-color: transparent;
  border-radius: 50%;
  border: none;
  height: 40px;
  width: 40px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  visibility: visible;
  &:hover {
    background: #f3f2f0;
  }
  > svg {
    margin-left: 2px;
  }
`

export const arrowButtonRight = css`
  background-color: transparent;
  border-radius: 50%;
  border: none;
  height: 40px;
  width: 40px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  visibility: visible;
  &:hover {
    background: #f3f2f0;
  }
  > svg {
    margin-right: 2px;
  }
`

export const arrowButtonHidden = css`
  visibility: hidden;
`

export const weeksContainer = ({ typography }: Theme) => css`
  display: flex;
  margin: 8px 0;
  justify-content: space-between;
`

export const singleWeeksContainer = css`
  justify-content: space-between;
`

export const monthsContainer = css`
  text-align: left;
  width: 100%;
`

export const singleMonthsContainer = css`
  width: 100%;
`

export const twoMonthsContainer = css`
  width: 42.5%;
`

export const calendarContainer = css`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
`

export const twoMonthCalendarContainer = css`
  justify-content: space-between;
`

export const dateButtonContainer = css`
  align-items: center;
  display: flex;
  justify-content: center;
  margin: 0;
  padding: 2px 0px;
  position: relative;
  /* border-radius: 50%; */
  overflow: hidden;
  transition: all 0.05s;
  flex-grow: 1;
`

export const dateButtonContainerDisabled = css`
  align-items: center;
  display: flex;
  justify-content: center;
  margin: 0;
  padding: 2px 0px;
  position: relative;
  border-radius: 50%;
  flex-grow: 1;

  div {
    &:hover {
      background: transparent;
      box-shadow: none;
    }
  }
`

export const dateButton = ({ colors, typography }: Theme) => css`
  width: 32px;
  height: 32px;
  border: none;
  align-items: center;
  display: flex;
  justify-content: center;
  background-color: inherit;
  cursor: pointer;
  color: ${colors.text};
  font-size: 14px;
  font-family: ${typography.bodyFontFamily};
  border-radius: 50%;

  &:hover {
    background: var(chakra-colors-gray-50);
  }
`

export const dateButtonDisabled = ({ colors }: Theme) => css`
  cursor: default;
  text-decoration: line-through;
  opacity: 0.6;

  color: ${colors.textLightest};

  &:hover {
    background: transparent;
  }
`

export const calendarHeaderContainer = css`
  display: flex;
  flex-direction: column;
`

export const calendarTitleContainer = ({ colors }: Theme) => css`
  display: flex;
  flex-direction: row;
  color: ${colors.textLight};
  margin-right: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const calendarTitle = ({ spacing, typography }: Theme) => css`
  font-size: ${typography.fontSize14};
  margin: 0 0 ${spacing.spacing1};
`

export const calendarTitleAndSubtitle = ({ spacing, typography }: Theme) => css`
  font-size: ${typography.fontSize12};
  margin: 0 0 ${spacing.spacing3};
`

export const calendarDateInput = ({ typography }: Theme) => css`
  height: 38px;
  font-family: ${typography.bodyFontFamily};
`

export const errorContainer = ({ colors, typography }: Theme) => css`
  display: flex;
  justify-content: space-between;
  padding-left: 10px;
  margin-top: 8px;
  width: 100%;
  > div {
    color: ${colors.errorColor};
    font-size: ${typography.fontSize12};
    font-weight: ${typography.fontWeightLight};
  }
`

export const monthYearContainer = ({ typography, colors }: Theme) => css`
  font-weight: bold;
  cursor: default;
  font-size: 15px;
  color: ${colors.text};
`

export const monthYearScrollContainer = css`
  font-weight: bold;
  cursor: default;
  font-size: 16px;
  line-height: 24px;
  margin-top: 40px;
`

export const horizontalRuleBottom = ({ colors, spacing }: Theme) => css`
  margin-bottom: 0px;
  margin-top: 8px;
  margin-left: -${spacing.spacing4};
  margin-right: -${spacing.spacing4};
  color: ${colors.background};
  background-color: inherit;
  border: none;
  height: 1px;
`

export const middleInputDash = ({ colors }: Theme) => css`
  margin-left: 5px;
  margin-right: 5px;
  color: ${colors.textLightest};
`

export const calendarMonthsTopContainer = () => css`
  display: flex;
  justify-content: center;
`

export const selectedStartDateOnlyButton = ({ colors }: Theme) => css`
  background: ${colors.primary};
  border-radius: 50px;
  color: white;
  position: relative;

  &:hover {
    background: ${colors.primaryHover};
    color: white;
  }
`

export const selectedStartDateButton = ({ colors }: Theme) => css`
  background: ${colors.primary};
  border-radius: 50px;
  color: white;
  position: relative;

  &:hover {
    background: ${colors.primaryHover};
    color: white;
  }

  ::before {
    content: '';
    height: 100%;
    position: absolute;
    top: 0;
    left: 50%;
    background: rgba(232, 70, 118, 0.1);
    width: 200%;
  }
`

export const selectedEndDateButton = ({ colors }: Theme) => css`
  background: ${colors.primary};
  border-radius: 50px;
  color: white;
  position: relative;

  &:hover {
    background: ${colors.primaryHover};
    color: white;
  }

  ::before {
    content: '';
    height: 100%;
    position: absolute;
    top: 0;
    right: 50%;
    background: rgba(232, 70, 118, 0.1);
    width: 200%;
  }
`

export const selectedRangeButton = ({ colors }: Theme) => css`
  background: rgba(232, 70, 118, 0.1);
  margin: 2px 0;
  padding: 0;

  > button {
    background: transparent;

    &:hover {
      background: ${colors.primaryHover};
      color: white;
    }
  }
`

export const todayDot = ({ colors }: Theme) => css`
  background: ${colors.primary};
  width: 4px;
  height: 4px;
  bottom: 5px;
  position: absolute;
  border-radius: 50%;
  top: 27px;
`

export const calendarDateInputsContainer = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0px 8px;
  height: 40px;
`

export const calendarDateInputsContainerOuter = css`
  display: flex;
  flex-direction: column;
  align-items: center;

  input[type='text'],
  input[type='email'],
  input[type='tel'],
  input[type='number'],
  input[type='password'],
  input[type='search'],
  input[type='date'] {
    width: 121px;
    height: 35px;
    display: flex;
    text-align: start;
    font-weight: 400;
    font-family: Moderat, Helvetica, sans-serif;
    outline: none;
    &:focus {
      border: 0px;
      font-family: Moderat, Helvetica, sans-serif;
    }
    padding: 12px 12px 12px 16px;
    border-radius: 6px;
    border: 0px;
    outline: none;
  }
`

export const dayOfWeek = ({ colors, typography }: Theme) => css`
  width: 32px;
  height: 32px;
  border: none;
  align-items: center;
  display: flex;
  justify-content: center;
  background-color: inherit;
  color: ${colors.text};
  font-size: ${typography.fontSize14};
`
